import { ThemeProvider } from "@material-ui/core/styles";
import React, { Suspense, useEffect, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import appTheme from "./config/MuiAppTheme";

import "./styles/inch-paper.css";

import { Route, Switch as RouterSwitch } from "react-router-dom";

import axios from "axios";
import AppToolbar from "./templates/toolbar/AppToolbar";

// import 'normalize.css';
import {
  get_db_base,
  isIdBundle,
  reportFrontendErrors
} from "./helpers/Functions";
import {
  ACContext,
  get_ac_cookie,
  request_ac_config,
  set_ac_cookie,
  validate_hash
} from "./helpers/access_code";
import wfeMuiTheme from "./projects/_modular/config/muiTheme";
import HTMLRenderer from "./templates/HTMLRenderer";
import ErrorBoundary from "./templates/components/errorBoundaries/ErrorBoundary";
import ErrorFallback from "./templates/components/errorBoundaries/ErrorFallback";
import {
  ExportStateContext,
  PageListContext,
  SelectedLocaleContext
} from "./templates/context";

import { updateQuery } from "./helpers/PosFunctions";

const POS = React.lazy(() => import("./projects/pos"));
const ModularProject = React.lazy(() => import("./projects/_modular"));
const BIJ = React.lazy(() => import("./projects/bij"));

const ModularProjectLandingPreview = React.lazy(() =>
  import("./projects/_modular/landing")
);

// to ensure query string for language is only grabbed once
let queried_lang = false;

export function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function getAC(query) {
  for (const [key, value] of query) {
    if (key === value) return key;
  }
  return "";
}

function ProjectValidator_memo() {
  const [loaded, setLoaded] = useState(false);
  const [acConfig, setACConfig] = useState(undefined);
  const [authAttempts, setAuthAttempts] = useState(0);
  const [authenticated, setAuthenticated] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pdfExportData, setToolbarData] = useState(null);
  const [error, setError] = useState(false);
  const [selectedContact, setContact] = useState(null);
  const [selectedAudience, setAudience] = useState(null);
  const [selectedLocale, setLocale] = useState(null);
  const [exposeContent, setExposeContent] = useState(null);
  const [idOfSinglePageElementToExport, setIdOfSinglePageElementToExport] =
    useState(null);
  // const [open, setOpen] = React.useState(false);
  const [invalidAC, setInvalidAC] = useState(false);
  const [validatedAC, setValidatedAC] = useState(null);
  //for POS
  const [exportState, setExportState] = useState({});
  const [pageList, setPageList] = useState([]);
  const [frontendErrors, setFrontendErrors] = useState([]);
  // for BIJ
  const [pagesToExport, setPagesToExport] = useState([]);
  const urlParams = { history: useHistory(), location: useLocation() };

  useEffect(() => {
    //report errors to sns topic
    const reportErrors = async () => {
      await reportFrontendErrors(frontendErrors);
    };
    reportErrors();
  }, [frontendErrors]);

  // const [enteredAccessCode, setEnteredAccessCode] = useState('');
  // const [rememberCode, setRememberCode] = useState(true);
  const params = useParams();

  // const handleClose = () => {
  //   setOpen(false);
  //   validateCode(enteredAccessCode)
  // };

  const toolbarActions = {
    setToolbarData,
    selectedContact,
    selectedAudience,
    setAudience,
    selectedLocale,
    setLocale,
    setIdOfSinglePageElementToExport,
    setExposeContent,
    exposeContent,
    pagesToExport,
    setPagesToExport
  };

  const toolbarData = {
    idOfSinglePageElementToExport,
    project: params.project
  };

  const toolbarsActionsAndData = {
    ...toolbarActions,
    ...toolbarData
  };

  // Collect route
  const access_code_enabled = acConfig?.preview_access === "access-code";

  useEffect(() => {
    if (params.project) {
      // Verify if project requires access code
      request_ac_config(params.project)
        .then((result) => setACConfig(result))
        .catch(() => setACConfig({}));
    } else {
      setACConfig({});
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const page = pagesToExport?.[0];
    //check if pagesToExport is in form [1] or {name: ProductName, id: 1, pages: [...pages]}
    const pageIsObject = page?.id ? true : false;
    //if list of objects, get the list of those IDs to add to search param
    //otherwise send null to the function to remove that search param
    const paramValue = pageIsObject ? pagesToExport.map((p) => p.id) : null;
    updateQuery(urlParams, { comparisons: paramValue });
  }, [pagesToExport]);

  const bundleIdPresent = isIdBundle(params.possibleBundleId);
  const skipAC = !!params.skipAC && bundleIdPresent;
  const query = useQuery();
  // const query_ac = unescape(query.get('ac') || '')
  const query_ac = getAC(query);

  useEffect(() => {
    let comparisons = query.get("comparisons");
    if (comparisons) {
      comparisons = comparisons.split(",");
      setPagesToExport(comparisons.map((c) => parseInt(c)));
    }

    // set initial value for selectedLocale from query string
    if (!queried_lang) {
      const langquery = query.get("lang");
      if (langquery) {
        if (!selectedLocale) {
          queried_lang = true;
          setLocale(langquery);
        } else if (typeof selectedLocale === "string") {
          if (langquery.toLowerCase() !== selectedLocale.toLowerCase()) {
            queried_lang = true;
            setLocale(langquery);
          }
        } else if (
          langquery.toLowerCase() !== selectedLocale.langquery.toLowerCase()
        ) {
          queried_lang = true;
          setLocale(langquery);
        }
      }
    }
  }, [query, selectedLocale]);

  // Handle access code authentication
  useEffect(() => {
    if (
      access_code_enabled === true &&
      !authenticated &&
      !invalidAC &&
      !skipAC
    ) {
      const existing_cookie = get_ac_cookie();
      const validate_cookie = existing_cookie.length > 0;
      const validate_code = query_ac.length > 0;
      const code_to_validate = validate_cookie ? existing_cookie : query_ac;
      const preserveAC = !!query.get("rememberAC") || false;
      const incrementAuthAttempts = () => setAuthAttempts(authAttempts + 1);
      const validateCode = (entered_access_code) => {
        if (!!entered_access_code && entered_access_code.length > 0) {
          // Validate against access code
          return validate_hash(
            params.project,
            entered_access_code,
            acConfig.access_hash
          )
            .then((ac_valid) => {
              if (ac_valid) {
                if (/* rememberCode && */ preserveAC)
                  set_ac_cookie(entered_access_code);

                setAuthenticated(true);
                return true;
              } else {
                return false;
              }
            })
            .finally(() => incrementAuthAttempts());
        }
      };
      if (code_to_validate.length > 0) {
        validateCode(code_to_validate).then((validity) => {
          if (validity) {
            setInvalidAC(false);
            setValidatedAC(code_to_validate);
          } else if (!validity && validate_cookie && validate_code) {
            validateCode(query_ac).then((validity) => {
              setInvalidAC(!validity);
              if (validity) setValidatedAC(query_ac);
            });
          } else setInvalidAC(true);
        });
      } else {
        setInvalidAC(true);
        // setOpen(true);
      }
    }
  }, [
    acConfig,
    authenticated,
    invalidAC,
    authAttempts,
    access_code_enabled,
    query_ac,
    skipAC,
    params.project,
    query
  ]);

  // Project not valid
  if (!params.project) {
    // report_pageview();
    return <React.Fragment />;
  }

  if (!submitted) {
    // Validate project
    if (params.project !== "pos") {
      axios
        .get(get_db_base(params.project))
        .then(() => setLoaded(true))
        .catch(() => setError(true));
    } else {
      setLoaded(true);
    }
    setSubmitted(true);
  }

  if (error) {
    // report_pageview();
    return <React.Fragment />;
  }

  if (acConfig === undefined) {
    return <React.Fragment />;
  }

  let show_spinner = !loaded || !pdfExportData;

  // if (pdfExportData)
  //   report_pageview();

  const globalSettings =
    pdfExportData?.additional?.dbDataRaw?.global_settings || {};

  const toolbarActionsAndData = {
    projectType: params.project,
    pdfExportData,

    setContact,
    selectedContact,
    setAudience,
    selectedAudience,
    setLocale,
    selectedLocale,
    setExposeContent,
    exposeContent,

    globalSettings,

    setIdOfSinglePageElementToExport,
    idOfSinglePageElementToExport
  };

  const should_auth = access_code_enabled === true && !authenticated;
  const validated_url_ac =
    access_code_enabled === true && validatedAC
      ? `${validatedAC}=${validatedAC}`
      : "";

  // Prefix project paths with "/preview" for production
  const prefixPaths = (paths = []) =>
    paths.map((path) => `${process.env.REACT_APP_ROUTE_PATH_PREFIX}${path}`);

  const isPosExport = !!exportState.primaryProduct && params.project === "pos";
  const mainClassNames = [
    idOfSinglePageElementToExport ? "spe" : "",
    isPosExport ? "enhancedExport" : ""
  ].join(" ");
  // const { project, possibleBundleId } = params;

  // Project loaded
  return (
    <ErrorBoundary
      fallback={<ErrorFallback />}
      setFrontendErrors={setFrontendErrors}
      frontendErrors={frontendErrors}
    >
      {
        should_auth && invalidAC && <p>Access code required</p>
        /*
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="form-dialog-title"
          disableBackdropClick={true}
          disableEscapeKeyDown={true}
        >
          <DialogTitle id="form-dialog-title">Please Enter Access Code</DialogTitle>
          <DialogContent>
            <DialogContentText></DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Access Code"
              fullWidth
              onChange={(e) => setEnteredAccessCode(e.target.value)}
            />
           <FormControlLabel
            checked={rememberCode}
            control={<Switch color="primary" />}
            label="Remember code"
            onChange={(e) => setRememberCode(e.target.checked)}
          />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Enter
            </Button>
          </DialogActions>
        </Dialog>
        */
      }
      {(!should_auth || skipAC) && (
        <ACContext.Provider value={{ url_ac: validated_url_ac }}>
          <SelectedLocaleContext.Provider value={{ selectedLocale, setLocale }}>
            <PageListContext.Provider value={{ pageList, setPageList }}>
              <ExportStateContext.Provider
                value={{ exportState, setExportState }}
              >
                {!!show_spinner && (
                  <img id="spinner" src="/spinner.gif" alt="..." />
                )}
                <ThemeProvider theme={appTheme}>
                  {
                    <AppToolbar
                      {...toolbarActionsAndData}
                      exportState={exportState}
                      setExportState={setExportState}
                    />
                  }
                  <main className={mainClassNames}>
                    <Suspense fallback={<div />}>
                      <RouterSwitch>
                        {/* {prefixPaths(["/:project/product-details/:id"]).map(
                        (path, path_idx) => {
                          toolbarsActionsAndData.posProductDetailsId =
                            possibleBundleId;
                          return (
                            <Route path={path} key={path_idx}>
                              {project === "pos" ? (
                                <POS {...toolbarsActionsAndData} />
                              ) : (
                                <React.Fragment></React.Fragment>
                              )}
                            </Route>
                          );
                        }
                      )} */}
                        {prefixPaths([
                          "/:project/landing/:bundleId?/:unused?"
                        ]).map((path, path_idx) => (
                          <Route path={path} key={path_idx}>
                            <ThemeProvider theme={wfeMuiTheme}>
                              <ModularProjectLandingPreview
                                {...toolbarsActionsAndData}
                              />
                            </ThemeProvider>
                          </Route>
                        ))}

                        {prefixPaths([
                          "/:project/:s3Key/:objectKey",
                          "/:project/:s3Key"
                        ]).map((path, path_idx) => (
                          <Route path={path} key={path_idx}>
                            <HTMLRenderer setToolbarData={setToolbarData} />
                          </Route>
                        ))}

                        {prefixPaths(["/:project"]).map((path, path_idx) => {
                          const { project } = params;
                          return (
                            <Route path={path} key={path_idx}>
                              {project === "pos" && (
                                <POS {...toolbarsActionsAndData} />
                              )}
                              {project === "wf-pro-hc" && (
                                <ModularProject {...toolbarsActionsAndData} />
                              )}
                              {project === "bij" && (
                                <BIJ {...toolbarsActionsAndData} />
                              )}
                            </Route>
                          );
                        })}
                      </RouterSwitch>
                    </Suspense>
                  </main>
                </ThemeProvider>
              </ExportStateContext.Provider>
            </PageListContext.Provider>
          </SelectedLocaleContext.Provider>
        </ACContext.Provider>
      )}
    </ErrorBoundary>
  );
}
const ProjectValidator = React.memo(ProjectValidator_memo);
export default ProjectValidator;
